/** @jsxImportSource @emotion/react */

import { Fragment, useEffect, useState } from "react";
import { match } from "ts-pattern";
import { Flex } from "../../utilities";
import { getValidChildren } from "../../utils";
import { LegacyTabList } from "../TabList";
import type { LegacyTabsProps } from "./Tabs.types";

const tabDirectionMap = {
  "vertical-rl": "row-reverse",
  "vertical-lr": "row",
  horizontal: "column",
} as const;

/** @deprecated Tabs 를 사용해주세요 */
export const LegacyTabs = <T extends string>({
  children,
  activeValue,
  size = "medium",
  direction = "horizontal",
  background = false,
  variant,
  onChangeValue,
  tabPanelStyle,
  tabListStyle,
  fullWidth = false,
  type = "primary",
}: LegacyTabsProps<T>) => {
  const [activeKey, setActiveKey] = useState<T>(activeValue ?? ("0" as T));

  useEffect(() => {
    if (activeValue) setActiveKey(activeValue);
  }, [activeValue]);

  useEffect(() => {
    if (onChangeValue) onChangeValue(activeKey);
  }, [activeKey, onChangeValue]);

  const getTabPanels = () => {
    const panels = getValidChildren(children).map((child, index) => {
      const value = child.props.value ?? index.toString();
      const isActive = value === activeKey;
      const isPreRender = child.props.preRender;

      if (isActive && child.props.children)
        return (
          <div
            key={value}
            id={`clay-tabpanel-${value}`}
            role="tabpanel"
            aria-labelledby={`tab-${value}`}
            tabIndex={0}
            css={tabPanelStyle}
          >
            {child.props.children}
          </div>
        );

      if (isPreRender)
        return (
          <div
            key={value}
            id={`clay-tabpanel-${value}`}
            role="tabpanel"
            aria-labelledby={`tab-${value}`}
            aria-hidden
            tabIndex={-1}
            css={{ display: "none", ...tabPanelStyle }}
          >
            {child.props.children}
          </div>
        );

      return <Fragment key={value} />;
    });

    return panels;
  };

  const tabListProps = match(variant)
    .with("segment", () => ({ variant, size, fullWidth, background, type }))
    .otherwise(() => ({ variant, size, fullWidth }));

  return (
    <Flex flexDirection={tabDirectionMap[direction]}>
      <LegacyTabList
        direction={direction === "horizontal" ? "horizontal" : "vertical"}
        activeValue={activeKey}
        onChangeValue={setActiveKey}
        sx={tabListStyle}
        {...tabListProps}
      >
        {children}
      </LegacyTabList>

      {getTabPanels()}
    </Flex>
  );
};
